import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gauge, LinkSimple, FolderSimple, Tag, Trash, PencilSimple, Plus, X, UploadSimple, DownloadSimple, CurrencyDollar, CursorClick, Users, ShoppingBag, BookOpen, RocketLaunch, MagnifyingGlass, Megaphone, GearSix, Crown, EnvelopeSimple, PaperPlaneRight } from "@phosphor-icons/react";

const emptyLink = { title: "", url: "", price: "", description: "", category_id: "", image_url: "", is_featured: false, is_deal_of_week: false };
const emptyCat = { name: "", description: "", image_url: "" };
const emptyTier = { name: "", days: 30, price: 15, active: true };
const emptyAdTier = { name: "", days: 7, price: 20, active: true };

function StatCard({ icon, label, value, color }) {
  return (
    <div className="border border-zinc-800 bg-[#18181B] p-5">
      <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-widest">{icon} {label}</div>
      <p className="font-mono font-extrabold text-3xl mt-2" style={{ color }}>{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const { user } = useAuth();
  const isFlagshipAdmin = user && user.role === "admin";

  const [stats, setStats] = useState(null);
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [ads, setAds] = useState([]);
  const [adTiers, setAdTiers] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [brandForm, setBrandForm] = useState({ brand_name: "", tagline: "" });
  const [licenseForm, setLicenseForm] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState({ type: "", text: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [emailForm, setEmailForm] = useState({ current_password: "", new_email: "" });
  const [emailMsg, setEmailMsg] = useState({ type: "", text: "" });
  const [emailSaving, setEmailSaving] = useState(false);
  const [storeSettings, setStoreSettings] = useState({ links: false, ads: false, platform: false });
  const [storeMsg, setStoreMsg] = useState("");
  useEffect(() => { if (tab === "settings") api.get("/store-settings").then(({ data }) => setStoreSettings(data)).catch(() => {}); }, [tab]);
  const saveStore = async (key, val) => {
    const next = { ...storeSettings, [key]: val };
    setStoreSettings(next);
    try { const { data } = await api.put("/admin/store-settings", next); setStoreSettings(data); setStoreMsg("Saved!"); setTimeout(() => setStoreMsg(""), 1500); }
    catch (e) { setStoreMsg("Save failed"); }
  };
  const [subs, setSubs] = useState(null);
  const [members, setMembers] = useState(null);
  const [newsMsg, setNewsMsg] = useState("");
  const [sending, setSending] = useState(false);

  const [linkForm, setLinkForm] = useState(null); // null=closed, obj=editing/new
  const [catForm, setCatForm] = useState(null);
  const [tierForm, setTierForm] = useState(null);
  const [adForm, setAdForm] = useState(null);
  const [adTierForm, setAdTierForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkResult, setBulkResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const loadAll = useCallback(async () => {
    const [s, l, c, t, a, at] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/links"), api.get("/categories"),
      api.get("/admin/pricing-tiers"), api.get("/admin/ads"), api.get("/admin/ad-tiers"),
    ]);
    setStats(s.data); setLinks(l.data); setCategories(c.data); setTiers(t.data); setAds(a.data); setAdTiers(at.data);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    api.get("/me/tenant").then((r) => { setTenant(r.data); setBrandForm({ brand_name: r.data.brand_name, tagline: r.data.tagline || "" }); });
    if (isFlagshipAdmin) api.get("/platform-license").then((r) => setLicenseForm(r.data));
  }, [isFlagshipAdmin]);

  const saveBrand = async () => {
    setSavedMsg("");
    try { const { data } = await api.put("/me/tenant", brandForm); setTenant(data); setSavedMsg("Brand saved!"); }
    catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const saveLicense = async () => {
    setSavedMsg("");
    try { await api.put("/admin/platform-license", licenseForm); setSavedMsg("License settings saved!"); }
    catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const changePassword = async () => {
    setPwMsg({ type: "", text: "" });
    if (pwForm.new_password.length < 6) { setPwMsg({ type: "err", text: "New password must be at least 6 characters." }); return; }
    if (pwForm.new_password !== pwForm.confirm) { setPwMsg({ type: "err", text: "New passwords do not match." }); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwMsg({ type: "ok", text: "Password changed successfully!" });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { setPwMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setPwSaving(false); }
  };
  const changeEmail = async () => {
    setEmailMsg({ type: "", text: "" });
    if (!emailForm.new_email || !emailForm.current_password) { setEmailMsg({ type: "err", text: "Enter your new email and current password." }); return; }
    setEmailSaving(true);
    try {
      const { data } = await api.post("/auth/change-email", emailForm);
      setEmailMsg({ type: "ok", text: `Login email updated to ${data.email}. Use it next time you log in.` });
      setEmailForm({ current_password: "", new_email: "" });
    } catch (e) { setEmailMsg({ type: "err", text: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setEmailSaving(false); }
  };


  const uploadImage = async (file, setter, obj) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter({ ...obj, image_url: data.url });
    } catch { setErr("Upload failed"); } finally { setUploading(false); }
  };

  // LINKS
  const saveLink = async () => {
    setErr("");
    try {
      const payload = { ...linkForm };
      if (linkForm.id) await api.put(`/links/${linkForm.id}`, payload);
      else await api.post("/links", payload);
      setLinkForm(null); loadAll();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const delLink = async (id) => { if (window.confirm("Delete this link?")) { await api.delete(`/links/${id}`); loadAll(); } };

  const exportLinks = () => {
    const esc = (v) => {
      const s = String(v == null ? "" : v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const cols = ["title", "url", "category", "price", "description", "image_url", "is_featured"];
    const lines = [cols.join(",")];
    links.forEach((l) => {
      lines.push([esc(l.title), esc(l.url), esc(l.category_slug), esc(l.price), esc(l.description), esc(l.image_url), l.is_featured ? "true" : "false"].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `affiliate-links-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onCsvFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setBulkText(String(e.target.result || ""));
    reader.readAsText(file);
  };
  const downloadTemplate = () => {
    const sample = 'title,url,category,price,description,image_url,is_featured\n' +
      '"Notion Pro","https://notion.so/?aff=you","Software & SaaS","$8/mo","All-in-one workspace","",true\n' +
      '"Ledger Nano X","https://ledger.com/?aff=you","Finance & Crypto","$149","Hardware crypto wallet","",false\n';
    const blob = new Blob([sample], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "affiliate-links-template.csv"; a.click();
    URL.revokeObjectURL(a.href);
  };
  const doBulkImport = async () => {
    setImporting(true); setBulkResult(null);
    try {
      const parsed = parseCSV(bulkText.trim());
      if (parsed.length < 2) { setBulkResult({ error: "Add a header row plus at least one data row." }); setImporting(false); return; }
      const headers = parsed[0].map((h) => h.trim().toLowerCase());
      const at = (name) => { const i = headers.indexOf(name); return i; };
      const rows = parsed.slice(1).map((cols) => ({
        title: (cols[at("title")] || "").trim(),
        url: (cols[at("url")] || "").trim(),
        category: (cols[at("category")] || "").trim(),
        price: (cols[at("price")] || "").trim(),
        description: (cols[at("description")] || "").trim(),
        image_url: (cols[at("image_url")] || "").trim(),
        is_featured: /^(true|yes|1)$/i.test((cols[at("is_featured")] || "").trim()),
      }));
      const { data } = await api.post("/links/bulk-import", { rows });
      setBulkResult(data);
      loadAll();
    } catch (e) {
      setBulkResult({ error: formatApiErrorDetail(e?.response?.data?.detail) });
    }
    setImporting(false);
  };

  // CATEGORIES
  const saveCat = async () => {
    setErr("");
    try {
      if (catForm.id) await api.put(`/categories/${catForm.id}`, catForm);
      else await api.post("/categories", catForm);
      setCatForm(null); loadAll();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const delCat = async (id) => { if (window.confirm("Delete category and all its links?")) { await api.delete(`/categories/${id}`); loadAll(); } };

  // TIERS
  const saveTier = async () => {
    setErr("");
    try {
      if (tierForm.id) await api.put(`/admin/pricing-tiers/${tierForm.id}`, tierForm);
      else await api.post("/admin/pricing-tiers", tierForm);
      setTierForm(null); loadAll();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const delTier = async (id) => { if (window.confirm("Delete this pricing plan?")) { await api.delete(`/admin/pricing-tiers/${id}`); loadAll(); } };

  // NEWSLETTER
  const loadSubs = useCallback(async () => {
    try { const { data } = await api.get("/admin/subscribers"); setSubs(data); }
    catch (e) { console.error("Failed to load subscribers:", e); }
  }, []);
  const loadMembers = useCallback(async () => {
    try { const { data } = await api.get("/admin/members"); setMembers(data); }
    catch (e) { console.error("Failed to load members:", e); }
  }, []);
  useEffect(() => { if (tab === "grow") { loadSubs(); loadMembers(); } }, [tab, loadSubs, loadMembers]);
  const deleteMember = async (id, email) => {
    if (!window.confirm(`Remove member ${email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/members/${id}`); loadMembers(); }
    catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove member"); }
  };
  const sendNewsletter = async () => {
    setNewsMsg(""); setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send");
      setNewsMsg(`Sent to ${data.sent} subscriber(s) with ${data.deals} deals!`);
    } catch (e) { setNewsMsg(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  // ADS
  const saveAd = async () => {
    setErr("");
    try {
      await api.post("/admin/ads", adForm);
      setAdForm(null); loadAll();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const delAd = async (id) => { if (window.confirm("Delete this ad?")) { await api.delete(`/admin/ads/${id}`); loadAll(); } };
  const saveAdTier = async () => {
    setErr("");
    try {
      if (adTierForm.id) await api.put(`/admin/ad-tiers/${adTierForm.id}`, adTierForm);
      else await api.post("/admin/ad-tiers", adTierForm);
      setAdTierForm(null); loadAll();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
  };
  const delAdTier = async (id) => { if (window.confirm("Delete this ad plan?")) { await api.delete(`/admin/ad-tiers/${id}`); loadAll(); } };

  const topLinks = [...links].sort((a, b) => (b.click_count || 0) - (a.click_count || 0)).filter((l) => (l.click_count || 0) > 0).slice(0, 5);

  const tabs = [
    { id: "overview", label: "Overview", icon: <Gauge size={16} weight="bold" /> },
    { id: "links", label: "Links", icon: <LinkSimple size={16} weight="bold" /> },
    { id: "categories", label: "Categories", icon: <FolderSimple size={16} weight="bold" /> },
    { id: "pricing", label: "Pricing", icon: <Tag size={16} weight="bold" /> },
    { id: "ads", label: "Ads", icon: <Megaphone size={16} weight="bold" /> },
    { id: "grow", label: "Grow", icon: <EnvelopeSimple size={16} weight="bold" /> },
    { id: "settings", label: "Settings", icon: <GearSix size={16} weight="bold" /> },
    { id: "guide", label: "Guide", icon: <BookOpen size={16} weight="bold" /> },
  ];

  const field = "w-full bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-2 text-white text-sm";

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight mb-6">Admin <span className="text-[#FF007F]">Dashboard</span></h1>

      <div className="flex flex-wrap gap-2 mb-8 border-b border-zinc-800 pb-4">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dash-tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${tab === t.id ? "bg-[#FF007F] text-white border-[#FF007F]" : "border-zinc-700 text-zinc-400 hover:border-[#00FFFF] hover:text-[#00FFFF]"}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {err && <p className="text-[#FF007F] text-sm mb-4 border border-[#FF007F] p-3">{err}</p>}

      {/* OVERVIEW */}
      {tab === "overview" && stats && (
        <div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-stats">
            <StatCard icon={<LinkSimple size={14} />} label="Total Links" value={stats.total_links} color="#00FFFF" />
            <StatCard icon={<ShoppingBag size={14} />} label="Sponsored" value={stats.sponsored_links} color="#FF007F" />
            <StatCard icon={<FolderSimple size={14} />} label="Categories" value={stats.categories} color="#FFD600" />
            <StatCard icon={<Users size={14} />} label="Members" value={stats.members} color="#39FF14" />
            <StatCard icon={<CurrencyDollar size={14} />} label="Revenue" value={`$${stats.revenue}`} color="#39FF14" />
            <StatCard icon={<ShoppingBag size={14} />} label="Paid Orders" value={stats.paid_orders} color="#FF007F" />
            <StatCard icon={<CursorClick size={14} />} label="Total Clicks" value={stats.total_clicks} color="#00FFFF" />
          </div>

          <div className="mt-10" data-testid="dash-top-links">
            <div className="flex items-center gap-2 mb-4">
              <CursorClick size={20} weight="bold" color="#00FFFF" />
              <h2 className="font-display text-xl font-black uppercase">Top Performing Links</h2>
            </div>
            {topLinks.length === 0 ? (
              <p className="text-zinc-500 text-sm">No clicks yet — once visitors start clicking your deals, your top earners show up here.</p>
            ) : (
              <div className="grid gap-2">
                {topLinks.map((l, i) => (
                  <div key={l.id} className="flex items-center gap-4 border border-zinc-800 bg-[#18181B] p-3" data-testid={`dash-top-link-${i}`}>
                    <span className="font-display font-black text-lg w-6 text-center" style={{ color: ["#FFD600", "#00FFFF", "#FF007F", "#39FF14", "#a1a1aa"][i] }}>{i + 1}</span>
                    <img src={mediaUrl(l.image_url) || "https://images.unsplash.com/photo-1774271074342-93257fc7486f?w=200&q=80"} alt="" className="w-12 h-12 object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold truncate">{l.title}</p>
                      <p className="text-xs text-zinc-500 truncate">{l.category_slug}{l.is_featured ? " · Featured" : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-extrabold text-[#00FFFF]">{l.click_count}</p>
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500">clicks</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* LINKS */}
      {tab === "links" && (
        <div>
          <div className="flex flex-wrap gap-3 mb-6">
            <button onClick={() => setLinkForm({ ...emptyLink })} data-testid="dash-add-link"
              className="flex items-center gap-1 px-4 py-2 bg-[#FF007F] text-white text-xs font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
              <Plus size={16} weight="bold" /> Add Link
            </button>
            <button onClick={() => { setBulkText(""); setBulkResult(null); setBulkOpen(true); }} data-testid="dash-bulk-import"
              className="flex items-center gap-1 px-4 py-2 bg-transparent text-[#00FFFF] text-xs font-bold uppercase tracking-widest border border-[#00FFFF] hover:bg-[#00FFFF] hover:text-black transition-colors">
              <UploadSimple size={16} weight="bold" /> Bulk Import (CSV)
            </button>
            <button onClick={exportLinks} disabled={links.length === 0} data-testid="dash-export-links"
              className="flex items-center gap-1 px-4 py-2 bg-transparent text-[#FFD600] text-xs font-bold uppercase tracking-widest border border-[#FFD600] hover:bg-[#FFD600] hover:text-black transition-colors disabled:opacity-40">
              <DownloadSimple size={16} weight="bold" /> Export CSV
            </button>
          </div>
          <div className="grid gap-3">
            {links.map((l) => (
              <div key={l.id} className="flex items-center gap-4 border border-zinc-800 bg-[#18181B] p-3">
                <img src={mediaUrl(l.image_url) || "https://images.unsplash.com/photo-1774271074342-93257fc7486f?w=200&q=80"} alt="" className="w-16 h-16 object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{l.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{l.category_slug} · {l.owner_type === "user" ? "SPONSORED" : "Admin"} · {l.click_count} clicks</p>
                </div>
                <button onClick={() => setLinkForm({ ...l })} className="p-2 border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF]" data-testid={`dash-edit-link-${l.id}`}><PencilSimple size={16} /></button>
                <button onClick={() => delLink(l.id)} className="p-2 border border-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F]" data-testid={`dash-del-link-${l.id}`}><Trash size={16} /></button>
              </div>
            ))}
            {links.length === 0 && <p className="text-zinc-500">No links yet.</p>}
          </div>
        </div>
      )}

      {/* CATEGORIES */}
      {tab === "categories" && (
        <div>
          <button onClick={() => setCatForm({ ...emptyCat })} data-testid="dash-add-category"
            className="flex items-center gap-1 mb-6 px-4 py-2 bg-[#FF007F] text-white text-xs font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
            <Plus size={16} weight="bold" /> Add Category
          </button>
          <div className="grid sm:grid-cols-2 gap-3">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-3 border border-zinc-800 bg-[#18181B] p-3">
                <div className="flex-1">
                  <p className="font-bold">{c.name}</p>
                  <p className="text-xs text-zinc-500">{c.link_count} links · /{c.slug}</p>
                </div>
                <button onClick={() => setCatForm({ ...c })} className="p-2 border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF]"><PencilSimple size={16} /></button>
                <button onClick={() => delCat(c.id)} className="p-2 border border-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F]"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PRICING */}
      {tab === "pricing" && (
        <div>
          <p className="text-zinc-400 text-sm mb-4">Change your plans anytime — updates apply instantly to the Post-a-Link page.</p>
          <button onClick={() => setTierForm({ ...emptyTier })} data-testid="dash-add-tier"
            className="flex items-center gap-1 mb-6 px-4 py-2 bg-[#FF007F] text-white text-xs font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
            <Plus size={16} weight="bold" /> Add Plan
          </button>
          <div className="grid sm:grid-cols-3 gap-4">
            {tiers.map((t) => (
              <div key={t.id} className="border border-zinc-800 bg-[#18181B] p-5">
                <div className="flex justify-between items-start">
                  <p className="text-xs uppercase tracking-widest text-zinc-400">{t.name}</p>
                  <span className={`text-[10px] font-mono px-2 py-0.5 ${t.active ? "bg-[#39FF14] text-black" : "bg-zinc-700 text-zinc-300"}`}>{t.active ? "ACTIVE" : "OFF"}</span>
                </div>
                <p className="font-mono font-extrabold text-3xl text-[#39FF14] mt-2">${t.price}</p>
                <p className="text-sm text-zinc-400">{t.days} days</p>
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setTierForm({ ...t })} className="flex-1 py-2 text-xs uppercase border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF]" data-testid={`dash-edit-tier-${t.id}`}>Edit</button>
                  <button onClick={() => delTier(t.id)} className="py-2 px-3 border border-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F]"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ADS */}
      {tab === "ads" && (
        <div className="space-y-10">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-black uppercase">Active & House Ads</h2>
              <button onClick={() => setAdForm({ title: "", image_url: "", target_url: "", placement: "home" })} data-testid="dash-add-ad"
                className="flex items-center gap-1 px-4 py-2 bg-[#FFD600] text-black text-xs font-bold uppercase tracking-widest border border-[#FFD600] hover:bg-transparent hover:text-[#FFD600] transition-colors">
                <Plus size={16} weight="bold" /> Add House Ad
              </button>
            </div>
            <p className="text-zinc-400 text-sm mb-4">Ads members buy appear here automatically. You can also add your own free "house ads" to fill empty banner space.</p>
            <div className="grid gap-3">
              {ads.map((a) => (
                <div key={a.id} className="flex items-center gap-4 border border-zinc-800 bg-[#18181B] p-3">
                  <img src={mediaUrl(a.image_url) || "https://images.unsplash.com/photo-1774271074342-93257fc7486f?w=200&q=80"} alt="" className="w-20 h-12 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{a.title}</p>
                    <p className="text-xs text-zinc-500 truncate">{a.owner_name} · {a.owner_type === "user" ? "PAID" : "House"} · {a.placement}{a.expires_at ? ` · expires ${a.expires_at.slice(0,10)}` : ""}</p>
                  </div>
                  <button onClick={() => delAd(a.id)} className="p-2 border border-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F]" data-testid={`dash-del-ad-${a.id}`}><Trash size={16} /></button>
                </div>
              ))}
              {ads.length === 0 && <p className="text-zinc-500">No ads running yet.</p>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-black uppercase">Ad Plans & Prices</h2>
              <button onClick={() => setAdTierForm({ ...emptyAdTier })} data-testid="dash-add-adtier"
                className="flex items-center gap-1 px-4 py-2 bg-[#FF007F] text-white text-xs font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors">
                <Plus size={16} weight="bold" /> Add Ad Plan
              </button>
            </div>
            <p className="text-zinc-400 text-sm mb-4">These are the prices advertisers pay for a banner. Change them anytime.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {adTiers.map((t) => (
                <div key={t.id} className="border border-zinc-800 bg-[#18181B] p-5">
                  <div className="flex justify-between items-start">
                    <p className="text-xs uppercase tracking-widest text-zinc-400">{t.name}</p>
                    <span className={`text-[10px] font-mono px-2 py-0.5 ${t.active ? "bg-[#39FF14] text-black" : "bg-zinc-700 text-zinc-300"}`}>{t.active ? "ACTIVE" : "OFF"}</span>
                  </div>
                  <p className="font-mono font-extrabold text-3xl text-[#39FF14] mt-2">${t.price}</p>
                  <p className="text-sm text-zinc-400">{t.days} days</p>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setAdTierForm({ ...t })} className="flex-1 py-2 text-xs uppercase border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF]" data-testid={`dash-edit-adtier-${t.id}`}>Edit</button>
                    <button onClick={() => delAdTier(t.id)} className="py-2 px-3 border border-zinc-700 hover:border-[#FF007F] hover:text-[#FF007F]"><Trash size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* GROW / NEWSLETTER */}
      {tab === "grow" && (
        <div className="max-w-2xl space-y-6" data-testid="dash-grow">
          <div className="border border-zinc-800 bg-[#18181B] p-6">
            <div className="flex items-center gap-2 mb-2"><EnvelopeSimple size={22} weight="fill" color="#00FFFF" /><h2 className="font-display text-xl font-black uppercase">Newsletter</h2></div>
            <p className="text-sm text-zinc-400 mb-4">Visitors subscribe from your storefront footer. Send them a "hottest deals" email blast anytime — it auto-includes your Deal of the Week and top deals.</p>
            <div className="flex items-center gap-6 mb-5">
              <div>
                <p className="font-mono font-extrabold text-4xl text-[#39FF14]" data-testid="subscriber-count">{subs ? subs.count : "…"}</p>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Subscribers</p>
              </div>
            </div>
            {newsMsg && <p className="text-[#39FF14] text-sm mb-3 border border-[#39FF14] p-2">{newsMsg}</p>}
            <button onClick={sendNewsletter} disabled={sending || !subs || subs.count === 0} data-testid="send-newsletter-button"
              className="flex items-center gap-2 px-5 py-3 bg-[#FF007F] text-white text-xs font-bold uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors disabled:opacity-50">
              <PaperPlaneRight size={16} weight="fill" /> {sending ? "Sending…" : "Send Deal Blast Now"}
            </button>
            {subs && subs.count === 0 && <p className="text-xs text-zinc-500 mt-2">No subscribers yet — share your site to get sign-ups!</p>}
          </div>
          {subs && subs.subscribers?.length > 0 && (
            <div className="border border-zinc-800 bg-[#18181B] p-6">
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Recent subscribers</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {subs.subscribers.slice(0, 50).map((s) => (
                  <div key={s.email} className="flex justify-between text-sm border-b border-zinc-800 pb-1">
                    <span className="text-zinc-300">{s.email}</span>
                    <span className="text-zinc-600 font-mono text-xs">{s.created_at?.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border border-zinc-800 bg-[#18181B] p-6" data-testid="dash-members">
            <div className="flex items-center gap-2 mb-2"><Users size={22} weight="fill" color="#FFD600" /><h2 className="font-display text-xl font-black uppercase">Members</h2></div>
            <p className="text-sm text-zinc-400 mb-4">People who registered accounts on your site (to post links or advertise). You can remove test or spam accounts here.</p>
            <p className="font-mono font-extrabold text-4xl text-[#FFD600] mb-4" data-testid="member-count">{members ? members.length : "…"}</p>
            {members && members.length === 0 && <p className="text-xs text-zinc-500">No members yet.</p>}
            {members && members.length > 0 && (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 text-sm border-b border-zinc-800 pb-2" data-testid={`member-row-${m.email}`}>
                    <div className="min-w-0">
                      <p className="text-zinc-300 truncate">{m.email}</p>
                      <p className="text-zinc-600 font-mono text-xs">{m.name || "—"} · joined {m.created_at?.slice(0, 10) || "n/a"}</p>
                    </div>
                    <button onClick={() => deleteMember(m.id, m.email)} data-testid={`member-delete-${m.email}`}
                      className="shrink-0 p-2 border border-zinc-700 text-zinc-400 hover:border-[#FF007F] hover:text-[#FF007F] transition-colors" title="Remove member">
                      <Trash size={15} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SETTINGS */}
      {tab === "settings" && (
        <div className="max-w-2xl space-y-8" data-testid="dash-settings">
          {savedMsg && <p className="text-[#39FF14] text-sm border border-[#39FF14] p-2">{savedMsg}</p>}
          <div className="border border-zinc-800 bg-[#18181B] p-6">
            <h2 className="font-display text-xl font-black uppercase mb-1">Your Storefront</h2>
            {tenant && (
              <p className="text-sm text-zinc-400 mb-4">
                Public address:{" "}
                <a href={tenant.is_flagship ? "/" : `/site/${tenant.slug}`} className="text-[#00FFFF] underline" data-testid="dash-storefront-link">
                  {tenant.is_flagship ? window.location.origin + "/" : window.location.origin + "/site/" + tenant.slug}
                </a>
              </p>
            )}
            <label className="text-xs uppercase tracking-widest text-zinc-400">Brand name</label>
            <input value={brandForm.brand_name} onChange={(e) => setBrandForm({ ...brandForm, brand_name: e.target.value })} data-testid="dash-brand-name"
              className={field + " mb-3"} />
            <label className="text-xs uppercase tracking-widest text-zinc-400">Tagline</label>
            <input value={brandForm.tagline} onChange={(e) => setBrandForm({ ...brandForm, tagline: e.target.value })} data-testid="dash-brand-tagline"
              className={field + " mb-4"} />
            <button onClick={saveBrand} data-testid="dash-save-brand" className="px-5 py-2 bg-[#FF007F] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#e60072]">Save Brand</button>
          </div>

          <div className="border border-zinc-800 bg-[#18181B] p-6" data-testid="dash-change-email">
            <h2 className="font-display text-xl font-black uppercase mb-1">Change Login Email</h2>
            <p className="text-sm text-zinc-400 mb-4">Your current login email is <span className="text-[#00FFFF]" data-testid="dash-current-email">{user?.email}</span>. Enter a new email and your current password to change it.</p>
            {emailMsg.text && <p className={`text-sm border p-2 mb-3 ${emailMsg.type === "ok" ? "text-[#39FF14] border-[#39FF14]" : "text-[#FF007F] border-[#FF007F]"}`} data-testid="dash-email-msg">{emailMsg.text}</p>}
            <label className="text-xs uppercase tracking-widest text-zinc-400">New login email</label>
            <input type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} data-testid="dash-email-new" className={field + " mb-3"} placeholder="you@yourdomain.com" />
            <label className="text-xs uppercase tracking-widest text-zinc-400">Current password</label>
            <input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} data-testid="dash-email-password" className={field + " mb-4"} />
            <button onClick={changeEmail} disabled={emailSaving} data-testid="dash-email-submit" className="px-5 py-2 bg-[#FFD600] text-black text-xs font-bold uppercase tracking-widest hover:bg-[#e6c200] disabled:opacity-50">
              {emailSaving ? "Saving…" : "Update Email"}
            </button>
          </div>

          <div className="border border-zinc-800 bg-[#18181B] p-6" data-testid="dash-payments-toggle">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-xl font-black uppercase">Payments / Store</h2>
              {storeMsg && <span className="text-[#39FF14] text-xs font-bold uppercase">{storeMsg}</span>}
            </div>
            <p className="text-sm text-zinc-400 mb-4">Turn purchasing on when you're ready to accept payments. While off, visitors see a "Coming Soon" message and can't buy.</p>
            {[
              { key: "links", label: "Paid Link Submissions", desc: "Let members pay to feature their affiliate links" },
              { key: "ads", label: "Banner Advertising", desc: "Let advertisers buy banner ad spots" },
              { key: "platform", label: "Buy the Platform (White-label)", desc: "Let people purchase their own site" },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between py-3 border-t border-zinc-800" data-testid={`store-toggle-row-${row.key}`}>
                <div>
                  <p className="font-bold text-sm">{row.label}</p>
                  <p className="text-xs text-zinc-500">{row.desc}</p>
                </div>
                <button onClick={() => saveStore(row.key, !storeSettings[row.key])} data-testid={`store-toggle-${row.key}`}
                  className={`relative w-14 h-7 rounded-full transition-colors ${storeSettings[row.key] ? "bg-[#39FF14]" : "bg-zinc-700"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${storeSettings[row.key] ? "translate-x-7" : ""}`} />
                </button>
              </div>
            ))}
          </div>


          <div className="border border-zinc-800 bg-[#18181B] p-6" data-testid="dash-change-password">
            <h2 className="font-display text-xl font-black uppercase mb-1">Change Password</h2>
            <p className="text-sm text-zinc-400 mb-4">Update your admin password. You'll stay logged in on this device.</p>
            {pwMsg.text && <p className={`text-sm border p-2 mb-3 ${pwMsg.type === "ok" ? "text-[#39FF14] border-[#39FF14]" : "text-[#FF007F] border-[#FF007F]"}`} data-testid="dash-pw-msg">{pwMsg.text}</p>}
            <label className="text-xs uppercase tracking-widest text-zinc-400">Current password</label>
            <input type="password" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="dash-pw-current" className={field + " mb-3"} />
            <label className="text-xs uppercase tracking-widest text-zinc-400">New password</label>
            <input type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="dash-pw-new" className={field + " mb-3"} />
            <label className="text-xs uppercase tracking-widest text-zinc-400">Confirm new password</label>
            <input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="dash-pw-confirm" className={field + " mb-4"} />
            <button onClick={changePassword} disabled={pwSaving} data-testid="dash-pw-submit" className="px-5 py-2 bg-[#00FFFF] text-black text-xs font-bold uppercase tracking-widest hover:bg-[#00cccc] disabled:opacity-50">
              {pwSaving ? "Saving…" : "Update Password"}
            </button>
          </div>

          {isFlagshipAdmin && licenseForm && (
            <div className="border-2 border-[#FFD600] bg-[#18181B] p-6">
              <div className="flex items-center gap-2 mb-1"><Crown size={22} weight="fill" color="#FFD600" /><h2 className="font-display text-xl font-black uppercase">Sell the Platform</h2></div>
              <p className="text-sm text-zinc-400 mb-4">Set the price and pitch for people who want to buy their own copy of your platform.</p>
              <label className="text-xs uppercase tracking-widest text-zinc-400">License price (USD)</label>
              <input type="number" step="0.01" value={licenseForm.price} onChange={(e) => setLicenseForm({ ...licenseForm, price: parseFloat(e.target.value) || 0 })} data-testid="dash-license-price" className={field + " mb-3"} />
              <label className="text-xs uppercase tracking-widest text-zinc-400">Headline</label>
              <input value={licenseForm.headline} onChange={(e) => setLicenseForm({ ...licenseForm, headline: e.target.value })} data-testid="dash-license-headline" className={field + " mb-3"} />
              <label className="text-xs uppercase tracking-widest text-zinc-400">Description</label>
              <textarea rows={3} value={licenseForm.description} onChange={(e) => setLicenseForm({ ...licenseForm, description: e.target.value })} data-testid="dash-license-desc" className={field + " mb-3"} />
              <label className="flex items-center gap-2 text-sm text-zinc-300 mb-4">
                <input type="checkbox" checked={licenseForm.active} onChange={(e) => setLicenseForm({ ...licenseForm, active: e.target.checked })} /> Offer available for purchase
              </label>
              <button onClick={saveLicense} data-testid="dash-save-license" className="px-5 py-2 bg-[#FFD600] text-black text-xs font-bold uppercase tracking-widest hover:bg-[#e6c200]">Save License Settings</button>
            </div>
          )}
        </div>
      )}

      {/* GUIDE */}
      {tab === "guide" && <GuideTab setTab={setTab} />}

      {/* AD MODAL */}
      {adForm && (
        <Modal title="Add House Ad" onClose={() => setAdForm(null)}>
          <div className="grid gap-3">
            <input className={field} placeholder="Headline" value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} data-testid="dash-ad-title" />
            <input className={field} placeholder="Destination URL" value={adForm.target_url} onChange={(e) => setAdForm({ ...adForm, target_url: e.target.value })} data-testid="dash-ad-url" />
            <div>
              <label className="text-xs text-zinc-400 flex items-center gap-2 mb-1"><UploadSimple size={14} /> Banner image upload or paste URL</label>
              <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], setAdForm, adForm)} className="text-sm text-zinc-400 file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#FFD600] file:text-black file:text-xs mb-2" />
              <input className={field} placeholder="https://image-url" value={adForm.image_url} onChange={(e) => setAdForm({ ...adForm, image_url: e.target.value })} />
              {uploading && <p className="text-xs text-[#FFD600] mt-1">Uploading…</p>}
            </div>
            <button onClick={saveAd} data-testid="dash-save-ad" className="py-3 bg-[#FFD600] text-black font-bold uppercase tracking-widest text-sm hover:bg-[#e6c200]">Save Ad</button>
          </div>
        </Modal>
      )}

      {/* AD TIER MODAL */}
      {adTierForm && (
        <Modal title={adTierForm.id ? "Edit Ad Plan" : "Add Ad Plan"} onClose={() => setAdTierForm(null)}>
          <div className="grid gap-3">
            <input className={field} placeholder="Plan name" value={adTierForm.name} onChange={(e) => setAdTierForm({ ...adTierForm, name: e.target.value })} data-testid="dash-adtier-name" />
            <label className="text-xs text-zinc-400">Duration (days)</label>
            <input className={field} type="number" value={adTierForm.days} onChange={(e) => setAdTierForm({ ...adTierForm, days: parseInt(e.target.value) || 0 })} data-testid="dash-adtier-days" />
            <label className="text-xs text-zinc-400">Price (USD)</label>
            <input className={field} type="number" step="0.01" value={adTierForm.price} onChange={(e) => setAdTierForm({ ...adTierForm, price: parseFloat(e.target.value) || 0 })} data-testid="dash-adtier-price" />
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={adTierForm.active} onChange={(e) => setAdTierForm({ ...adTierForm, active: e.target.checked })} /> Active
            </label>
            <button onClick={saveAdTier} data-testid="dash-save-adtier" className="py-3 bg-[#FF007F] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#e60072]">Save Plan</button>
          </div>
        </Modal>
      )}

      {/* BULK IMPORT MODAL */}
      {bulkOpen && (
        <Modal title="Bulk Import Links (CSV)" onClose={() => setBulkOpen(false)}>
          <div className="grid gap-3">
            <p className="text-sm text-zinc-400">
              Paste CSV rows or upload a <span className="text-[#00FFFF]">.csv</span> file. Required columns:
              <span className="font-mono text-zinc-200"> title, url, category</span>. Optional:
              <span className="font-mono text-zinc-200"> price, description, image_url, is_featured</span>.
              New categories are created automatically.
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={downloadTemplate} data-testid="dash-bulk-template"
                className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 hover:border-[#FFD600] hover:text-[#FFD600] transition-colors">
                Download template
              </button>
              <label className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors cursor-pointer">
                Upload CSV file
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onCsvFile(e.target.files[0])} data-testid="dash-bulk-file" />
              </label>
            </div>
            <textarea rows={8} className={field + " font-mono text-xs"} data-testid="dash-bulk-textarea"
              placeholder={"title,url,category,price,description,image_url,is_featured\nNotion Pro,https://notion.so/?aff=you,Software & SaaS,$8/mo,All-in-one workspace,,true"}
              value={bulkText} onChange={(e) => setBulkText(e.target.value)} />
            {bulkResult && (
              <div data-testid="dash-bulk-result" className={`p-3 text-sm border ${bulkResult.error ? "border-[#FF007F] text-[#FF007F]" : "border-[#39FF14] text-zinc-200"}`}>
                {bulkResult.error ? bulkResult.error : (
                  <>
                    <p className="text-[#39FF14] font-bold">Imported {bulkResult.created} of {bulkResult.total} links.</p>
                    {bulkResult.skipped > 0 && (
                      <p className="mt-1 text-[#FFD600]">{bulkResult.skipped} duplicate {bulkResult.skipped === 1 ? "link" : "links"} skipped (URL already exists).</p>
                    )}
                    {bulkResult.errors && bulkResult.errors.length > 0 && (
                      <ul className="mt-2 list-disc pl-5 text-zinc-400 text-xs">
                        {bulkResult.errors.map((er) => <li key={`row-${er.row}`}>Row {er.row}: {er.error}</li>)}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}
            <button onClick={doBulkImport} disabled={importing} data-testid="dash-bulk-submit"
              className="py-3 bg-[#00FFFF] text-black font-bold uppercase tracking-widest text-sm hover:bg-[#00cccc] disabled:opacity-50">
              {importing ? "Importing…" : "Import Links"}
            </button>
          </div>
        </Modal>
      )}

      {/* LINK MODAL */}
      {linkForm && (
        <Modal title={linkForm.id ? "Edit Link" : "Add Link"} onClose={() => setLinkForm(null)}>
          <div className="grid gap-3">
            <input className={field} placeholder="Title" value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })} data-testid="dash-link-title" />
            <input className={field} placeholder="Affiliate URL" value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} data-testid="dash-link-url" />
            <input className={field} placeholder="Price / Offer (e.g. $49.99)" value={linkForm.price} onChange={(e) => setLinkForm({ ...linkForm, price: e.target.value })} />
            <select className={field} value={linkForm.category_id} onChange={(e) => setLinkForm({ ...linkForm, category_id: e.target.value })} data-testid="dash-link-category">
              <option value="">Select category…</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <textarea className={field} placeholder="Description" rows={3} value={linkForm.description} onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })} />
            <div>
              <label className="text-xs text-zinc-400 flex items-center gap-2 mb-1"><UploadSimple size={14} /> Image upload or paste URL below</label>
              <input type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files[0], setLinkForm, linkForm)} className="text-sm text-zinc-400 file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#FF007F] file:text-white file:text-xs mb-2" />
              <input className={field} placeholder="https://image-url" value={linkForm.image_url} onChange={(e) => setLinkForm({ ...linkForm, image_url: e.target.value })} />
              {uploading && <p className="text-xs text-[#00FFFF] mt-1">Uploading…</p>}
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={linkForm.is_featured} onChange={(e) => setLinkForm({ ...linkForm, is_featured: e.target.checked })} /> Featured deal
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" data-testid="dash-link-dealofweek" checked={linkForm.is_deal_of_week} onChange={(e) => setLinkForm({ ...linkForm, is_deal_of_week: e.target.checked })} /> ⭐ Deal of the Week (big homepage spotlight)
            </label>
            <button onClick={saveLink} data-testid="dash-save-link" className="py-3 bg-[#FF007F] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#e60072]">Save Link</button>
          </div>
        </Modal>
      )}

      {/* CATEGORY MODAL */}
      {catForm && (
        <Modal title={catForm.id ? "Edit Category" : "Add Category"} onClose={() => setCatForm(null)}>
          <div className="grid gap-3">
            <input className={field} placeholder="Category name" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} data-testid="dash-cat-name" />
            <textarea className={field} placeholder="Description" rows={2} value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })} />
            <input className={field} placeholder="Banner image URL (optional)" value={catForm.image_url} onChange={(e) => setCatForm({ ...catForm, image_url: e.target.value })} />
            <button onClick={saveCat} data-testid="dash-save-cat" className="py-3 bg-[#FF007F] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#e60072]">Save Category</button>
          </div>
        </Modal>
      )}

      {/* TIER MODAL */}
      {tierForm && (
        <Modal title={tierForm.id ? "Edit Plan" : "Add Plan"} onClose={() => setTierForm(null)}>
          <div className="grid gap-3">
            <input className={field} placeholder="Plan name" value={tierForm.name} onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })} data-testid="dash-tier-name" />
            <label className="text-xs text-zinc-400">Duration (days)</label>
            <input className={field} type="number" value={tierForm.days} onChange={(e) => setTierForm({ ...tierForm, days: parseInt(e.target.value) || 0 })} data-testid="dash-tier-days" />
            <label className="text-xs text-zinc-400">Price (USD)</label>
            <input className={field} type="number" step="0.01" value={tierForm.price} onChange={(e) => setTierForm({ ...tierForm, price: parseFloat(e.target.value) || 0 })} data-testid="dash-tier-price" />
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={tierForm.active} onChange={(e) => setTierForm({ ...tierForm, active: e.target.checked })} /> Active (shown to members)
            </label>
            <button onClick={saveTier} data-testid="dash-save-tier" className="py-3 bg-[#FF007F] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#e60072]">Save Plan</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function GuideTab({ setTab }) {
  const [copied, setCopied] = useState("");
  const SITEMAP_URL = "https://affiliatedconnections.store/api/sitemap.xml";
  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(""), 2000); }).catch(() => {});
  };
  const gscSteps = [
    { t: "Go to Google Search Console", b: "Open search.google.com/search-console and sign in with your Google account. Click 'Add property'." },
    { t: "Choose the URL prefix option", b: "In the popup, use the RIGHT-hand box labelled 'URL prefix' and enter your full site address exactly: https://affiliatedconnections.store — then click Continue." },
    { t: "Pick the 'HTML tag' method", b: "Google will show verification options. Expand 'HTML tag'. Good news — your verification code is ALREADY installed on your site, so you can skip the copy step here. (Just make sure you've redeployed after this update.)" },
    { t: "Redeploy, then click Verify", b: "Hit Redeploy so the verification code goes live, wait for it to finish, then return to Search Console and click 'Verify'. Google should confirm ownership within a few seconds." },
    { t: "Submit your sitemap", b: "Once verified, open the 'Sitemaps' section in the left menu, paste your sitemap address below, and click Submit. This tells Google about every deal and category page to crawl." },
    { t: "Wait for indexing", b: "Indexing takes a few days to a couple of weeks. Check back in Search Console under 'Pages' and 'Performance' to watch your deals start appearing in Google search." },
  ];
  const steps = [
    { icon: <FolderSimple size={22} weight="bold" color="#FFD600" />, title: "1. Set up your categories", body: "Go to the Categories tab. You already have 6 niches seeded (Tech, Health, Fashion, Home, Software, Finance). Add, rename, or delete any to match your niches. Each category becomes its own SEO-friendly page.", action: () => setTab("categories"), cta: "Open Categories" },
    { icon: <LinkSimple size={22} weight="bold" color="#00FFFF" />, title: "2. Add your affiliate links", body: "In the Links tab, click 'Add Link'. Paste your affiliate URL, add a title, price/offer, description, and an image (upload or paste a URL). Pick the category. Mark hot ones as 'Featured' to push them to the top. Fill in a few great deals to make the site look alive.", action: () => setTab("links"), cta: "Add Links" },
    { icon: <MagnifyingGlass size={22} weight="bold" color="#39FF14" />, title: "3. Get found on search engines (free traffic)", body: "Use clear, keyword-rich titles and descriptions (e.g. 'Best Budget Wireless Earbuds — 40% Off'). The more real deals, reviews and comments you have, the more pages search engines can index. Share your category page links on social media to kickstart traffic.", action: null },
    { icon: <Tag size={22} weight="bold" color="#FF007F" />, title: "4. Turn on your income streams", body: "You make money two ways: (a) commissions from your own affiliate links when visitors click 'Visit' and buy, and (b) fees from members who pay to feature their links. Set your plan prices in the Pricing tab — change them anytime.", action: () => setTab("pricing"), cta: "Edit Pricing" },
    { icon: <Users size={22} weight="bold" color="#FFD600" />, title: "5. Let members pay to post", body: "Members register for free, then use 'Post a Link' to submit their own affiliate link and pay via Stripe (cards + Cash App Pay). Their link goes live with a glowing SPONSORED badge and expires automatically when their plan ends. You keep 100% of the fee.", action: null },
    { icon: <Gauge size={22} weight="bold" color="#00FFFF" />, title: "6. Watch it grow", body: "The Overview tab shows total links, sponsored links, members, paid orders, revenue and clicks. Check it daily. More clicks = more commissions; more sponsored links = more fees.", action: () => setTab("overview"), cta: "Open Overview" },
  ];
  return (
    <div className="max-w-3xl" data-testid="dash-guide">
      <div className="border-2 border-[#FF007F] bg-[#18181B] p-6 mb-8 hard-shadow">
        <div className="flex items-center gap-3">
          <RocketLaunch size={30} weight="fill" color="#FF007F" />
          <h2 className="font-display text-2xl font-black uppercase">Start Making Money — Your Playbook</h2>
        </div>
        <p className="text-zinc-400 mt-3">Follow these 6 steps in order. You'll have a live, money-ready affiliate hub in under an hour.</p>
      </div>
      <div className="space-y-4">
        {steps.map((s) => (
          <div key={s.title} className="border border-zinc-800 bg-[#0f0f12] p-5">
            <div className="flex items-start gap-4">
              <div className="mt-1">{s.icon}</div>
              <div className="flex-1">
                <h3 className="font-display font-bold text-lg">{s.title}</h3>
                <p className="text-zinc-400 text-sm mt-1 leading-relaxed">{s.body}</p>
                {s.action && (
                  <button onClick={s.action} className="mt-3 px-4 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 hover:border-[#00FFFF] hover:text-[#00FFFF] transition-colors">
                    {s.cta} →
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="border border-[#00FFFF] bg-[#18181B] p-5 mt-8">
        <div className="flex items-center gap-2 mb-2"><Megaphone size={20} weight="fill" color="#00FFFF" /><h3 className="font-display font-bold uppercase">Pro tips to earn faster</h3></div>
        <ul className="text-sm text-zinc-400 space-y-2 list-disc pl-5">
          <li>Post at least 12–20 quality deals before promoting — an empty store converts poorly.</li>
          <li>Refresh deals weekly so returning visitors always find something new.</li>
          <li>Reply to comments and reviews — engagement builds trust and repeat clicks.</li>
          <li>Offer a cheap intro plan ($15) to attract your first paying advertisers, then raise prices.</li>
        </ul>
      </div>

      <div className="border-2 border-[#39FF14] bg-[#18181B] p-6 mt-8" data-testid="dash-guide-gsc">
        <div className="flex items-center gap-2 mb-2">
          <MagnifyingGlass size={24} weight="bold" color="#39FF14" />
          <h3 className="font-display text-xl font-black uppercase">Get on Google — Search Console Setup</h3>
        </div>
        <p className="text-zinc-400 text-sm mb-3">Your site is already SEO-optimized and your Google verification code is installed. Follow these steps to connect Google Search Console so your deals get indexed and show up in search results.</p>
        <div className="flex items-center gap-2 mb-5 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#39FF14]/10 border border-[#39FF14] text-[#39FF14] font-bold uppercase tracking-widest">✓ Verification code installed</span>
          <span className="text-zinc-500">Remember to Redeploy so it goes live.</span>
        </div>
        <ol className="space-y-4">
          {gscSteps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-[#39FF14] text-black font-black text-sm">{i + 1}</span>
              <div>
                <h4 className="font-bold text-sm text-white">{s.t}</h4>
                <p className="text-zinc-400 text-sm mt-0.5 leading-relaxed">{s.b}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 border border-zinc-700 bg-[#0f0f12] p-4">
          <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Your sitemap address (paste this into Search Console → Sitemaps)</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-[#00FFFF] break-all font-mono">{SITEMAP_URL}</code>
            <button onClick={() => copy(SITEMAP_URL, "sitemap")} data-testid="copy-sitemap-button"
              className="flex-shrink-0 px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-700 hover:border-[#39FF14] hover:text-[#39FF14] transition-colors">
              {copied === "sitemap" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
        <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" data-testid="open-gsc-button"
          className="inline-flex items-center gap-2 mt-4 px-5 py-3 bg-[#39FF14] text-black font-black uppercase tracking-widest text-sm hover:bg-transparent hover:text-[#39FF14] border border-[#39FF14] transition-colors">
          Open Google Search Console →
        </a>
      </div>
    </div>
  );
}

function parseCSV(text) {
  const rows = [];
  let field = "", row = [], inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field); rows.push(row); row = []; field = "";
    } else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim() !== ""));
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0f0f12] border-2 border-[#FF007F] w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h3 className="font-display font-black uppercase">{title}</h3>
          <button onClick={onClose} className="p-1 hover:text-[#FF007F]"><X size={22} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
